/**
 * Icon set for the Emailforge admin UI — exact SVG paths from the design export
 * (`_design-reference/components/shared.jsx`). Stroke weight 1.3, 16×16 viewBox.
 */

type IconProps = {
  size?: number;
  className?: string;
  title?: string;
};

const base = {
  width: 15,
  height: 15,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.3,
} as const;

const baseBlock = { ...base, strokeWidth: 1.4, width: 14, height: 14 } as const;

export const Icon = {
  mail: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="M2.5 4.5l5.5 4.5L13.5 4.5" />
    </svg>
  ),
  grid: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <rect x="2.5" y="2.5" width="4.5" height="4.5" rx="0.5" />
      <rect x="9" y="2.5" width="4.5" height="4.5" rx="0.5" />
      <rect x="2.5" y="9" width="4.5" height="4.5" rx="0.5" />
      <rect x="9" y="9" width="4.5" height="4.5" rx="0.5" />
    </svg>
  ),
  send: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M14 2L2 7l5 2 2 5z" />
      <path d="M7 9l7-7" />
    </svg>
  ),
  code: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M5.5 5L2 8l3.5 3M10.5 5L14 8l-3.5 3M9.5 3.5l-3 9" />
    </svg>
  ),
  beaker: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M6 2v4L3 12.5A1 1 0 0 0 3.9 14h8.2a1 1 0 0 0 .9-1.5L10 6V2M5 2h6M5.5 9.5h5" />
    </svg>
  ),
  bolt: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M9 1.5L3 9h4l-1 5.5L12 7H8z" />
    </svg>
  ),
  plug: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M5 9l2 2-2.5 2.5a1.5 1.5 0 0 1-2-2L5 9zM5 9l6-6M11 3l2 2M9 1l2 2M13 5l2 2M11 3l2 2" />
    </svg>
  ),
  palette: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <circle cx="8" cy="8" r="6" />
      <circle cx="5.5" cy="6" r="0.8" fill="currentColor" />
      <circle cx="8" cy="4.5" r="0.8" fill="currentColor" />
      <circle cx="10.5" cy="6" r="0.8" fill="currentColor" />
      <circle cx="11" cy="9" r="0.8" fill="currentColor" />
      <path d="M8 14c-1 0-1.5-1-1-2 .5-1 0-2-1-2" />
    </svg>
  ),
  gear: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M3 13l1.5-1.5M11.5 4.5L13 3" />
    </svg>
  ),
  search: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  ),
  plus: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M8 3v10M3 8h10" />
    </svg>
  ),
  chev: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M6 4l4 4-4 4" />
    </svg>
  ),
  chevDown: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M4 6l4 4 4-4" />
    </svg>
  ),
  check: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title} strokeWidth={1.6}>
      <path d="M3 8.5L6.5 12 13 4.5" />
    </svg>
  ),
  x: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  ),
  eye: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" />
      <circle cx="8" cy="8" r="1.75" />
    </svg>
  ),
  folder: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M1.5 4.5A1 1 0 0 1 2.5 3.5h3l1.5 1.5h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-7.5z" />
    </svg>
  ),
  users: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <circle cx="6" cy="6" r="2.5" />
      <path d="M1.5 13c0-2.2 2-3.5 4.5-3.5s4.5 1.3 4.5 3.5M10 4a2 2 0 1 1 0 4M14.5 12.5c0-1.8-1.4-2.7-3-3" />
    </svg>
  ),
  bell: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M4 7a4 4 0 0 1 8 0c0 2.5.5 4 1.5 5h-11c1-1 1.5-2.5 1.5-5zM6.5 13a1.5 1.5 0 0 0 3 0" />
    </svg>
  ),
  layers: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M8 1.5l6 3-6 3-6-3 6-3zM2 8l6 3 6-3M2 11l6 3 6-3" />
    </svg>
  ),
  doc: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M3.5 1.5h6l3 3v10a0.5 0.5 0 0 1-0.5.5h-8.5a0.5 0.5 0 0 1-0.5-0.5V2a0.5 0.5 0 0 1 0.5-0.5zM9.5 1.5V4.5h3" />
    </svg>
  ),
  drag: ({ size = 15, className, title }: IconProps) => (
    <svg viewBox="0 0 16 16" width={size} height={size} fill="currentColor" className={className} aria-label={title}>
      <circle cx="5.5" cy="3.5" r="1" />
      <circle cx="10.5" cy="3.5" r="1" />
      <circle cx="5.5" cy="8" r="1" />
      <circle cx="10.5" cy="8" r="1" />
      <circle cx="5.5" cy="12.5" r="1" />
      <circle cx="10.5" cy="12.5" r="1" />
    </svg>
  ),
  grip: ({ size = 12, className, title }: IconProps) => (
    <svg viewBox="0 0 16 16" width={size} height={size} fill="currentColor" className={className} aria-label={title}>
      <circle cx="5.5" cy="3.5" r="0.9" />
      <circle cx="10.5" cy="3.5" r="0.9" />
      <circle cx="5.5" cy="8" r="0.9" />
      <circle cx="10.5" cy="8" r="0.9" />
      <circle cx="5.5" cy="12.5" r="0.9" />
      <circle cx="10.5" cy="12.5" r="0.9" />
    </svg>
  ),
  copy: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1" />
      <path d="M10.5 5.5V3a0.5 0.5 0 0 0-0.5-0.5H3A0.5 0.5 0 0 0 2.5 3v7a0.5 0.5 0 0 0 0.5 0.5h2.5" />
    </svg>
  ),
  trash: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M2.5 4h11M6 4V2.5h4V4M4 4l0.5 9.5a0.5 0.5 0 0 0 0.5.5h6a0.5 0.5 0 0 0 0.5-0.5L12 4" />
    </svg>
  ),
  link: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M7 9a3 3 0 0 0 4 0l2.5-2.5a3 3 0 0 0-4-4L8.5 3.5" />
      <path d="M9 7a3 3 0 0 0-4 0L2.5 9.5a3 3 0 0 0 4 4L7.5 12.5" />
    </svg>
  ),
  flag: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M3 2v12" />
      <path d="M3 2.5s2-1 4 0 4 1 6 0v6s-4 1-6 0-4 0-4 0V2.5z" />
    </svg>
  ),
  download: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M8 2v9M4.5 7.5L8 11l3.5-3.5M2.5 13h11" />
    </svg>
  ),
  upload: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M8 11V2M4.5 5.5L8 2l3.5 3.5M2.5 13h11" />
    </svg>
  ),
  history: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M2.5 8a5.5 5.5 0 1 0 1.8-4.1" />
      <path d="M2 2.5V5h2.5" />
      <path d="M8 5v3l2 1.5" />
    </svg>
  ),
  moon: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M13.5 9.5A5.5 5.5 0 1 1 6.5 2.5 4.5 4.5 0 0 0 13.5 9.5z" />
    </svg>
  ),
  sun: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.5 3.5l1 1M11.5 11.5l1 1M3.5 12.5l1-1M11.5 4.5l1-1" />
    </svg>
  ),
  monitor: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <rect x="1.5" y="2.5" width="13" height="9" rx="1" />
      <path d="M5 14h6M8 11.5V14" />
    </svg>
  ),
  phone: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <rect x="4.5" y="1.5" width="7" height="13" rx="1.5" />
      <path d="M7 13h2" />
    </svg>
  ),
  undo: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M6 4L2.5 7 6 10M3 7h7.5a3 3 0 0 1 0 6H8" />
    </svg>
  ),
  redo: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M10 4L13.5 7 10 10M13 7H5.5a3 3 0 0 0 0 6H8" />
    </svg>
  ),
  flame: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M8 1.5c1 2 4 3.5 4 7a4 4 0 1 1-8 0c0-1 .5-2 1.5-2.5C5.5 7.5 6 8 7 8c-.5-2 1-4 1-6.5z" />
    </svg>
  ),
  zap: ({ size = 15, className, title }: IconProps) => (
    <svg viewBox="0 0 16 16" width={size} height={size} fill="currentColor" className={className} aria-label={title}>
      <path d="M9 1.5L3 9h3.5l-.5 5.5L12 7H8.5z" />
    </svg>
  ),
  arrow: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  ),
  arrowUp: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M8 13V3M4 7l4-4 4 4" />
    </svg>
  ),
  arrowDown: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M8 3v10M4 9l4 4 4-4" />
    </svg>
  ),
  star: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M8 2l1.8 3.8 4.2.5-3 2.9.8 4.3L8 11.5 4.2 13.5 5 9.2 2 6.3l4.2-.5z" />
    </svg>
  ),
  filter: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M2 3.5h12l-4.5 5v4.5l-3 1V8.5z" />
    </svg>
  ),
  more: ({ size = 15, className, title }: IconProps) => (
    <svg viewBox="0 0 16 16" width={size} height={size} fill="currentColor" className={className} aria-label={title}>
      <circle cx="4" cy="8" r="1.1" />
      <circle cx="8" cy="8" r="1.1" />
      <circle cx="12" cy="8" r="1.1" />
    </svg>
  ),
  ab: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M2 12V4l2.5 6L7 4v8M9 12V4h3.2a1.8 1.8 0 0 1 0 3.6H9M9 8h3.5a1.8 1.8 0 0 1 0 4H9" />
    </svg>
  ),
  variable: ({ size = 15, className, title }: IconProps) => (
    <svg {...base} width={size} height={size} className={className} aria-label={title}>
      <path d="M3.5 3C2.5 5 2.5 11 3.5 13M12.5 3c1 2 1 8 0 10M6 10l2-4 2 4M6.5 8.5h3" />
    </svg>
  ),
  heading: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <path d="M3 3v10M13 3v10M3 8h10" />
    </svg>
  ),
  text: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <path d="M3 4h10M3 8h10M3 12h6" />
    </svg>
  ),
  image: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <rect x="2" y="3" width="12" height="10" rx="1" />
      <circle cx="6" cy="7" r="1" />
      <path d="M2 11l4-3 3 2 5-4" />
    </svg>
  ),
  button: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <rect x="2.5" y="5.5" width="11" height="5" rx="2.5" />
      <path d="M6 8h4" />
    </svg>
  ),
  columns: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <rect x="2" y="3" width="5" height="10" rx="0.5" />
      <rect x="9" y="3" width="5" height="10" rx="0.5" />
    </svg>
  ),
  divider: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <path d="M2 8h12M2 4h2M12 4h2M2 12h2M12 12h2" />
    </svg>
  ),
  spacer: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <path d="M3 4h10M3 12h10M8 6v4" />
    </svg>
  ),
  social: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <circle cx="4" cy="8" r="2" />
      <circle cx="12" cy="4.5" r="2" />
      <circle cx="12" cy="11.5" r="2" />
      <path d="M5.7 7l4.7-2M5.7 9l4.7 2" />
    </svg>
  ),
  product: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <path d="M2.5 4l5.5-2.5L13.5 4v8L8 14.5 2.5 12zM2.5 4L8 6.5l5.5-2.5M8 6.5V14" />
    </svg>
  ),
  footer: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <rect x="2" y="3" width="12" height="10" rx="1" />
      <path d="M2 10h12M5 12h2" />
    </svg>
  ),
  hero: ({ size = 14, className, title }: IconProps) => (
    <svg {...baseBlock} width={size} height={size} className={className} aria-label={title}>
      <rect x="2" y="3" width="12" height="10" rx="1" />
      <path d="M2 9h12M5.5 11h2" />
    </svg>
  ),
};

export type IconName = keyof typeof Icon;
